/**
 * ActivityCapturePolicy — admin/manager settings page.
 *
 * Route: /admin/activity-policy
 *
 * Lets a manager set each capture source to Off / Advisory / Enforced.
 * Reads + writes /api/tenant/activity-capture-settings. Saving is optimistic
 * with revert-on-error.
 */

import { useEffect, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { tokens } from "@/components/operator/tokens";
import {
  useActivityCaptureSettings,
  type ActivityCaptureSettings,
  type CaptureMode,
} from "@/hooks/useActivityCaptureSettings";

type SourceKey = keyof ActivityCaptureSettings;

interface SourceDef {
  key: SourceKey;
  label: string;
  blurb: string;
}

const SOURCES: SourceDef[] = [
  {
    key: "outlook_email",
    label: "Outlook email",
    blurb:
      "Captures outbound and inbound email between reps and external contacts via Microsoft Graph.",
  },
  {
    key: "outlook_calendar",
    label: "Outlook calendar",
    blurb:
      "Captures meetings booked and held with external attendees. First-meeting detection auto-counts a 90-day-cold contact.",
  },
  {
    key: "teams_phone",
    label: "Teams phone",
    blurb:
      "Captures call records (date, duration, internal/external, contact match) from Microsoft Teams Phone.",
  },
  {
    key: "linkedin_extension",
    label: "LinkedIn extension",
    blurb:
      "Captures LinkedIn messages and connection requests via a browser extension. Off by default.",
  },
  {
    key: "dialer_third_party",
    label: "Third-party dialer",
    blurb:
      "Captures call records from Aircall / Orum / Dialpad / Nooks via webhook. Off unless a non-Teams dialer is in use.",
  },
];

const MODES: { key: CaptureMode; label: string; blurb: string }[] = [
  {
    key: "off",
    label: "Off",
    blurb: "Source is not captured. Nothing rendered in rep or manager UI.",
  },
  {
    key: "advisory",
    label: "Advisory",
    blurb:
      "Source is captured. Reps see their day at a glance. Nothing surfaces to managers.",
  },
  {
    key: "enforced",
    label: "Enforced",
    blurb:
      "Source is captured. Managers see self-reported vs system-observed in Activity audit. Reps must note offline work when there is a positive delta.",
  },
];

export default function ActivityCapturePolicy(): JSX.Element {
  const { settings, loading, error, refetch } = useActivityCaptureSettings();
  const [draft, setDraft] = useState<ActivityCaptureSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    if (settings) setDraft({ ...settings });
  }, [settings]);

  function update(key: SourceKey, mode: CaptureMode) {
    if (!draft) return;
    setDraft({ ...draft, [key]: mode });
    setSaveOk(false);
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      const res = await fetch("/api/tenant/activity-capture-settings", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: draft }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      setSaveOk(true);
      await refetch();
    } catch (e: any) {
      setSaveError(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Activity Capture Policy" }]} pageTitle="Activity Capture Policy" pageMeta="Admin settings">
    <main
      style={{
        maxWidth: 880,
        margin: "0 auto",
        padding: "32px 24px",
        fontFamily: tokens.font.body,
        color: tokens.color.textPrimary,
      }}
    >
      <header style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontFamily: tokens.font.display,
            fontSize: 26,
            fontWeight: 700,
            margin: 0,
            color: tokens.color.textPrimary,
          }}
        >
          Activity capture policy
        </h1>
        <p
          style={{
            fontSize: 14,
            color: tokens.color.textMuted,
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          Choose how much help the system gives your reps. Advisory shows reps
          their day at a glance. Enforced lets managers see the gap between
          self-reported and observed activity.
        </p>
      </header>

      {loading && (
        <div style={{ fontSize: 13, color: tokens.color.textMuted }}>Loading settings…</div>
      )}

      {error && !loading && (
        <div
          style={{
            padding: 10,
            fontSize: 13,
            color: tokens.color.danger,
            background: tokens.color.dangerBg,
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {draft && (
        <>
          {SOURCES.map((src) => (
            <section
              key={src.key}
              style={{
                marginBottom: 20,
                padding: 18,
                background: tokens.color.canvas,
                border: `1px solid ${tokens.color.border}`,
                borderRadius: tokens.radius.card,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15 }}>{src.label}</div>
              <div
                style={{
                  fontSize: 12,
                  color: tokens.color.textMuted,
                  marginTop: 4,
                  marginBottom: 12,
                }}
              >
                {src.blurb}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {MODES.map((m) => (
                  <label
                    key={m.key}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: 10,
                      borderRadius: 6,
                      border: `1px solid ${
                        draft[src.key] === m.key ? "#1F2937" : tokens.color.borderSoft
                      }`,
                      background:
                        draft[src.key] === m.key ? "#EFF6FF" : tokens.color.canvas,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name={src.key}
                      value={m.key}
                      checked={draft[src.key] === m.key}
                      onChange={() => update(src.key, m.key)}
                      data-testid={`policy-${src.key}-${m.key}`}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</div>
                      <div style={{ fontSize: 12, color: tokens.color.textMuted }}>
                        {m.blurb}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </section>
          ))}

          <footer
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 12,
            }}
          >
            <span style={{ fontSize: 12, color: tokens.color.textMuted }}>
              {saveOk
                ? "Saved."
                : saveError
                ? saveError
                : "Manager or admin role required to save."}
            </span>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{
                background: "#1F2937",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: 6,
                border: "none",
                fontWeight: 600,
                fontSize: 13,
                cursor: saving ? "not-allowed" : "pointer",
                fontFamily: tokens.font.body,
              }}
              data-testid="policy-save"
            >
              {saving ? "Saving…" : "Save policy"}
            </button>
          </footer>
        </>
      )}
    </main>
    </OperatorShell>
  );
}
